/**
 * One voice for every "the person you called is not here" outcome.
 *
 * The server sends the same p2p_target_offline shape for three different
 * situations and the app used to blur them into "did not answer":
 *
 *   - reason 'no_answer'    the call rang for the full window and nobody
 *                           picked up. deliveryReason says whether their
 *                           device was ever alerted.
 *   - reason 'do_not_disturb' (p2p_target_dnd)
 *   - anything else         the server could not even start the ring.
 *
 * The distinction matters to the caller ("Devin ignored me" vs "Devin's
 * phone never rang") and to ops: 'notification_provider_not_configured' is
 * a server-side misconfiguration, not a person being away. The caller still
 * gets person-level words for it — what happened to their call, not which
 * part of our infrastructure is missing; ops reads the deliveryReason code.
 */
const DELIVERY_DETAIL = {
    no_registered_device: 'Their phone is not set up for call alerts yet, so it may not have rung.',
    notification_provider_not_configured: 'Their phone could not be alerted this time, so it did not ring.',
    notification_delivery_failed: 'We could not alert their phone.',
    notification_rate_limited: 'Call alerts to them are paused for a moment.',
    notification_dispatch_unavailable: 'Call alerts were unavailable, so their phone may not have rung.',
    recipient_offline: 'They were not online.'
};
export function describeP2PUnavailable(data, appName = 'the app') {
    const name = data?.calleeName || 'That contact';
    const isGroup = data?.groupCall === true || (data?.participantCount || 0) > 2;
    const voicemail = data?.voicemailAvailable === true;
    const reason = data?.reason || '';
    const deliveryReason = data?.deliveryReason || '';
    const detail = deliveryReason && deliveryReason !== 'no_answer' ? DELIVERY_DETAIL[deliveryReason] || '' : '';
    const voicemailLine = voicemail ? ' You can leave a VideoMail message.' : '';
    if (reason === 'do_not_disturb' || reason === 'all_recipients_dnd') {
        const title = isGroup ? 'Everyone has Do Not Disturb on' : `${name} has Do Not Disturb on`;
        return { detail: '', message: `${title}.${voicemailLine}`, title };
    }
    if (reason === 'no_answer') {
        const title = `${name} didn't answer`;
        return {
            detail,
            message: `${title}.${detail ? ` ${detail}` : ''}${voicemailLine}`,
            title
        };
    }
    if (isGroup) {
        const title = `None of the selected ${appName} contacts is available right now`;
        return { detail, message: `${title}.`, title };
    }
    const title = `${name} could not be reached right now`;
    return { detail, message: `${title}.${detail ? ` ${detail}` : ''}${voicemailLine}`, title };
}
/** Status line while an outgoing direct call is ringing. */
export function describeP2PRinging(data) {
    if (data?.delivery === 'unconfirmed') {
        return `Trying to reach ${data?.calleeName || 'them'}…`;
    }
    return 'Ringing…';
}
